import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { refresh } from '../../services/auth.service';
import Spinner from '../../components/layout/Spinner';
import FadeIn from '../../utils/fadein';
import { FaEllipsisV, FaTimes } from 'react-icons/fa';
import { Clock } from 'lucide-react';
import { getCompanyLaunches, updateLaunchRankings, getLaunchReport } from '../../utils/api/admin';

function LaunchReport() {
  const [loaded, setLoaded] = useState(false);
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [selectedLaunch, setSelectedLaunch] = useState('all');
  const [showSaveBanner, setShowSaveBanner] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showSellersOnly, setShowSellersOnly] = useState(false);
  const [showBuyersOnly, setShowBuyersOnly] = useState(false);
  const imageUrl = process.env.REACT_APP_IMAGE_URL;
  const baseImageUrl = process.env.REACT_APP_BASE_IMAGE_URL;
  const screenshotUrl = process.env.REACT_APP_BASE_IMAGE_URL + 'companyscreenshots/';

  useEffect(() => {
    async function loadData(){
      let res = await getLaunchReport();
      if (res.success){
        setData(res.data);
        setFilteredData(res.data);
        setLoaded(true);
      }
      else{
        setLoaded(true);
        alert(res.message);
      }
    }

    loadData();
  }, []);

  const getLaunchNames = () => {
    const launches = [...new Set(data.map(item => item.launch_name))];
    return launches;
  };

  const handleLaunchFilter = (e) => {
    const launch = e.target.value;
    setSelectedLaunch(launch);
    filterData(launch, showSellersOnly, showBuyersOnly);
  };

  const handleSellerFilter = (e) => {
    const sellersOnly = e.target.checked;
    setShowSellersOnly(sellersOnly);
    if (sellersOnly) setShowBuyersOnly(false);
    filterData(selectedLaunch, sellersOnly, false);
  };

  const handleBuyerFilter = (e) => {
    const buyersOnly = e.target.checked;
    setShowBuyersOnly(buyersOnly);
    if (buyersOnly) setShowSellersOnly(false);
    filterData(selectedLaunch, false, buyersOnly);
  };

  const filterData = (launch, sellersOnly, buyersOnly) => {
    let filtered = [...data];
    
    if (launch !== 'all') {
      filtered = filtered.filter(item => item.launch_name === launch);
    }
    
    if (sellersOnly) {
      filtered = filtered.filter(item => item.is_seller);
    }
    
    if (buyersOnly) {
      filtered = filtered.filter(item => item.is_buyer);
    }
    
    setFilteredData(filtered);
  };

  function loadSaveBanner(){
    setShowSaveBanner(true);
    const timer = setTimeout(() => {
      setShowSaveBanner(false);
    }, 3000);
    return () => clearTimeout(timer);
  }

  async function updateRankings(){
    let res = await updateLaunchRankings(selectedLaunch);

    if (res.success){
      alert("done");
      // setData(res.data);
      // setFilteredData(res.data);
      // loadSaveBanner();
    }
    else{
      alert(res.message);
    }
  }

  const getRootDomain = (url) => {
    try {
      const hostname = new URL(url).hostname;
      return hostname.replace('www.', '');
    } catch {
      return url;
    }
  };

  const getDaysAgo = (date) => {
    const diff = new Date().getTime() - new Date(date).getTime();
    return Math.floor(diff / (1000 * 60 * 60 * 24));
  };

  return (
    <>
      <Helmet>
        <title>LeadrPro - Discover new software</title>
      </Helmet>

      <h1>Company Launches {`(${filteredData.length})`}</h1>

      {loaded===false && (
        <Spinner />
      )}

      {loaded && (
         <div>
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Total Sellers</h3>
              <p className="text-3xl font-bold text-purple-600">
                {data.filter(item => item.is_seller).length}
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Total Buyers</h3>
              <p className="text-3xl font-bold text-purple-600">
                {data.filter(item => item.is_buyer).length}
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Total Demo Applications</h3>
              <p className="text-3xl font-bold text-blue-600">
                {data.reduce((sum, item) => sum + (parseInt(item.demo_applications) || 0), 0)}
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Total Approved</h3>
              <p className="text-3xl font-bold text-green-600">
                {data.reduce((sum, item) => sum + (parseInt(item.demo_approved) || 0), 0)}
              </p>
            </div>
          </div>

          <div className="mb-4 flex items-center gap-4">
            <select 
              value={selectedLaunch}
              onChange={(e) => handleLaunchFilter(e)}
              className="border rounded px-3 py-1"
            >
              <option value="all">All Launches</option>
              {getLaunchNames().map((launch, index) => (
                <option key={index} value={launch}>{launch}</option>
              ))}
            </select>

            <label className="inline-flex items-center">
              <input
                type="checkbox"
                className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                checked={showSellersOnly}
                onChange={handleSellerFilter}
              />
              <span className="ml-2">Show Sellers Only</span>
            </label>

            <label className="inline-flex items-center">
              <input
                type="checkbox"
                className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                checked={showBuyersOnly}
                onChange={handleBuyerFilter}
              />
              <span className="ml-2">Show Buyers Only</span>
            </label>

            <button className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
              onClick={() => updateRankings()}
            >
              Update Rankings
            </button>
          </div>

          <div className="relative overflow-auto h-[600px] border border-gray-200 rounded-lg">
            <table className='admindashboard__table table w-full'>
              <thead className="sticky top-0 bg-white shadow-sm">
                <tr>
                  <th className="bg-white">Logo</th>
                  <th className="bg-white">Name</th>
                  <th className="bg-white">Date Created</th>
                  <th className="bg-white">Company ID</th>
                  <th className="bg-white">Seller</th>
                  <th className="bg-white">Demo Apps</th>
                  <th className="bg-white">Approved</th>
                  <th className="bg-white">Declined</th>
                  <th className="bg-white">Launch</th>
                  <th className="bg-white">Activity Score</th>
                  <th className="bg-white">Last Active</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map((item, index) => (
                  <tr key={index}>
                    <td>
                      <img className='w-10 h-10' src={imageUrl + 'tr:di-@@companylogos@@default-buyer.png/' + item.logo} />
                    </td>

                    <td>
                      <div className='text-md font-medium text-gray-900 cursor-pointer hover:underline'>
                        {item.name}
                      </div>

                      <div className='text-sm text-gray-500'>
                        {item.website && getRootDomain(item.website)}
                      </div>

                      <div className='text-sm text-gray-500'>
                        {item.company_size}
                      </div>

                      <div className='text-sm text-gray-500'>
                        {item.seller_email}
                      </div>
                      </td>

                    <td>{new Date(item.date_created).toLocaleDateString()}</td>
                    <td>{item.company_id}</td>
                    <td>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                        ${item.is_seller ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                        {item.is_seller ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td>
                      <span className="font-medium">{item.demo_applications || 0}</span>
                    </td>
                    <td>
                      <span className="font-medium text-green-600">{item.demo_approved || 0}</span>
                    </td>

                    <td>
                      <span className="font-medium text-red-600">{item.demos_declined || 0}</span>
                    </td>

                    <td>
                      <span className="text-sm text-gray-600">{item.launch_name || '-'}</span>
                    </td>
                    <td>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                        ${parseFloat(item.activity_score) >= 7 ? 'bg-green-100 text-green-800' : 
                          parseFloat(item.activity_score) >= 4 ? 'bg-yellow-100 text-yellow-800' : 
                          'bg-red-100 text-red-800'}`}>
                        {parseFloat(item.activity_score).toFixed(1)}
                      </span>
                    </td>
                    <td>
                      <div className="flex items-center gap-1.5 text-gray-500">
                        <Clock className="h-4 w-4" />
                        <div>
                          <span className="text-sm">
                            {(() => {
                              const lastActivity = item.last_activity ? new Date(item.last_activity) : null;
                              const lastLogin = item.last_login ? new Date(item.last_login) : null;
                              if (!lastActivity && !lastLogin) return '-';
                              
                              const latestDate = !lastActivity ? lastLogin :
                                               !lastLogin ? lastActivity :
                                               new Date(Math.max(lastActivity, lastLogin));
                              
                              const daysAgo = getDaysAgo(latestDate);
                              return (
                                <>
                                  {latestDate.toLocaleDateString()}
                                  <span className="text-xs text-gray-400 ml-1">
                                    ({daysAgo} {daysAgo === 1 ? 'day' : 'days'} ago)
                                  </span>
                                </>
                              );
                            })()}
                          </span>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
         </div>
        </div>
      )}

    {showModal && (
        <>
          <div className='overlay'></div>
          <div className='modal'>
            <div className='modal__close' onClick={() => setShowModal(false)}>
              <FaTimes />
            </div>

            <h2 className='modal__h2'>Admin Note</h2>

            <div className='mt-10'>
            
            </div>
          </div>
        </>
      )}

      {showDetailsModal && selectedItem && (
        <>
          <div className='overlay'></div>
          <div className='modal'>
            <div className='modal__close' onClick={() => setShowDetailsModal(false)}>
              <FaTimes />
            </div>
            <h2 className='modal__h2'>Launch Details</h2>
            <div className='mt-4 space-y-4'>
              <p><strong>Company:</strong> {selectedItem.name}</p>
              <p><strong>Launch Name:</strong> {selectedItem.launch_name}</p>
              <p><strong>Website:</strong> <a href={selectedItem.website} target="_blank" rel="noopener noreferrer" 
                className="text-blue-600 hover:text-blue-800">{selectedItem.website}</a></p>
              <p><strong>Status:</strong> {selectedItem.status}</p>
              <p><strong>Launch Status:</strong> {selectedItem.launch_status}</p>
              <p><strong>Launch Period:</strong> {new Date(selectedItem.launch_date_start).toLocaleDateString()} - {new Date(selectedItem.launch_date_end).toLocaleDateString()}</p>
            </div>
          </div>
        </>
      )}

      {showSaveBanner && (
        <FadeIn delay={150} duration={300}>
          <div className='profileinfo__saved-banner'>
            Profile updated successfully!
          </div>
        </FadeIn>
      )}
    </>
  );
}

export default LaunchReport;